import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface EmailRequest {
  to: string;
  subject: string;
  body: string;
  cc?: string[];
  bcc?: string[];
  attachments?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class EmailService {
  private emailApiUrl = 'http://172.27.34.66:8091/api/send-email';

  constructor(private http: HttpClient) { }

  /**
   * Sends an email using the email API
   * @param emailRequest The email request containing recipient, subject, and body
   * @returns An Observable with the API response
   */
  sendEmail(emailRequest: EmailRequest): Observable<any> {
    return this.http.post(this.emailApiUrl, emailRequest);
  }

  /**
   * Sends a confirmation email to a user who submitted a complaint
   * @param userEmail The email address of the user
   * @param userName The name of the user
   * @param complaintDetails The details of the complaint
   * @returns An Observable with the API response
   */
  sendUserConfirmation(userEmail: string, userName: string, complaintDetails: any): Observable<any> {
    const emailRequest: EmailRequest = {
      to: userEmail,
      subject: 'Your Complaint Has Been Received - TelOne Customer Support',
      body: this.generateUserEmailBody(userName, complaintDetails)
    };

    return this.sendEmail(emailRequest);
  }

  /**
   * Sends a notification email to customer support about a new complaint
   * @param userDetails The details of the user who submitted the complaint
   * @param complaintDetails The details of the complaint
   * @returns An Observable with the API response
   */
  sendSupportNotification(userDetails: any, complaintDetails: any): Observable<any> {
    // You would typically set this to your customer support email address
    const supportEmail = 'blakesuchiha@gmail.com';

    // TODO: Get the real complaints email

    const emailRequest: EmailRequest = {
      to: supportEmail,
      subject: 'New Customer Complaint Submitted - Urgent Action Required',
      body: this.generateSupportEmailBody(userDetails, complaintDetails)
    };

    return this.sendEmail(emailRequest);
  }

  /**
   * Generates the HTML body for the user confirmation email
   * @param userName The name of the user
   * @param complaintDetails The details of the complaint
   * @returns HTML string for the email body
   */
  private generateUserEmailBody(userName: string, complaintDetails: any): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
          }
          .container {
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            border: 1px solid #ddd;
            border-radius: 5px;
          }
          .header {
            background-color: #0056b3;
            color: white;
            padding: 15px;
            text-align: center;
            border-radius: 5px 5px 0 0;
          }
          .content {
            padding: 20px;
          }
          .footer {
            background-color: #f5f5f5;
            padding: 10px;
            text-align: center;
            font-size: 12px;
            border-radius: 0 0 5px 5px;
          }
          .button {
            display: inline-block;
            background-color: #0056b3;
            color: white;
            padding: 10px 20px;
            text-decoration: none;
            border-radius: 5px;
            margin-top: 15px;
          }
          .reference {
            background-color: #f9f9f9;
            padding: 10px;
            border-left: 4px solid #0056b3;
            margin: 15px 0;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h2>TelOne Customer Support</h2>
          </div>
          <div class="content">
            <p>Dear ${userName},</p>
            <p>Thank you for bringing your concern to our attention. We have successfully received your complaint and are committed to resolving it promptly.</p>

            <div class="reference">
              <p><strong>Reference Number:</strong> COMP-${Date.now().toString().substring(5)}</p>
              <p><strong>Submission Date:</strong> ${new Date().toLocaleDateString()}</p>
            </div>

            <p>Here's a summary of your complaint:</p>
            <ul>
              <li><strong>Account Name:</strong> ${complaintDetails.accountName}</li>
              <li><strong>Contact Number:</strong> ${complaintDetails.mobileNumber}</li>
              ${complaintDetails.exchange ? `<li><strong>Exchange:</strong> ${complaintDetails.exchange}</li>` : ''}
            </ul>

            <p>Our customer support team will review your complaint and respond within 48 hours. If your matter requires immediate attention, please contact our customer service hotline at 0800-###-###.</p>

            <p>We appreciate your patience and thank you for helping us improve our services.</p>

            <p>Regards,<br>TelOne Customer Support Team</p>
          </div>
          <div class="footer">
            <p>This is an automated email. Please do not reply to this message.</p>
            <p>© ${new Date().getFullYear()} TelOne. All rights reserved.</p>
            <p>
              <a href="https://www.telone.co.zw/privacy-policy">Privacy Policy</a> |
              <a href="https://www.telone.co.zw/terms-of-service">Terms of Service</a>
            </p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Generates the HTML body for the support notification email
   * @param userDetails The details of the user
   * @param complaintDetails The details of the complaint
   * @returns HTML string for the email body
   */
  private generateSupportEmailBody(userDetails: any, complaintDetails: any): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
          }
          .container {
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            border: 1px solid #ddd;
            border-radius: 5px;
          }
          .header {
            background-color: #d9534f;
            color: white;
            padding: 15px;
            text-align: center;
            border-radius: 5px 5px 0 0;
          }
          .content {
            padding: 20px;
          }
          .footer {
            background-color: #f5f5f5;
            padding: 10px;
            text-align: center;
            font-size: 12px;
            border-radius: 0 0 5px 5px;
          }
          .customer-info {
            background-color: #f9f9f9;
            padding: 15px;
            margin-bottom: 15px;
            border-radius: 5px;
          }
          .complaint-details {
            background-color: #f9f9f9;
            padding: 15px;
            margin-bottom: 15px;
            border-radius: 5px;
          }
          .action-required {
            background-color: #fff3cd;
            color: #856404;
            padding: 10px;
            border-radius: 5px;
            margin: 15px 0;
            font-weight: bold;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            padding: 8px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          th {
            background-color: #f2f2f2;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h2>New Customer Complaint - Urgent Action Required</h2>
          </div>
          <div class="content">
            <div class="action-required">
              A new customer complaint has been submitted and requires your immediate attention.
            </div>

            <h3>Customer Information</h3>
            <div class="customer-info">
              <table>
                <tr>
                  <th>Account Name:</th>
                  <td>${userDetails.accountName}</td>
                </tr>
                <tr>
                  <th>Mobile Number:</th>
                  <td>${userDetails.mobileNumber}</td>
                </tr>
                <tr>
                  <th>Email:</th>
                  <td>${userDetails.email}</td>
                </tr>
                ${userDetails.landlineNumber ? `
                <tr>
                  <th>Landline Number:</th>
                  <td>${userDetails.landlineNumber}</td>
                </tr>` : ''}
                ${userDetails.exchange ? `
                <tr>
                  <th>Exchange:</th>
                  <td>${userDetails.exchange}</td>
                </tr>` : ''}
              </table>
            </div>

            <h3>Complaint Details</h3>
            <div class="complaint-details">
              <p><strong>Reference Number:</strong> COMP-${Date.now().toString().substring(5)}</p>
              <p><strong>Submission Date:</strong> ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
              <p><strong>Customer Feedback:</strong></p>
              <p style="white-space: pre-line; background-color: #eef; padding: 10px; border-radius: 5px;">${complaintDetails.customerFeedback}</p>
            </div>

            <h3>Required Actions</h3>
            <ol>
              <li>Review the complaint within 24 hours</li>
              <li>Contact the customer to acknowledge their complaint</li>
              <li>Document the case in the Customer Management System</li>
              <li>Escalate to relevant department if necessary</li>
              <li>Resolve within 48 hours or provide a progress update</li>
            </ol>

            <p>Please remember to update the status of this complaint in the system once you begin processing it.</p>
          </div>
          <div class="footer">
            <p>This is an automated notification from the TelOne Customer Complaint System.</p>
            <p>© ${new Date().getFullYear()} TelOne. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }
}
